import type { Participant } from "@vc/models"

/**
 * Whether a tile has a camera picture to draw, rather than the avatar.
 *
 * Local participant: MediaStreamContext supplies the shared camera stream.
 * Remote participant: `participant.stream`, the real SFU consumer stream.
 *
 * An explicit `stream` always wins, local or not. Your own additional camera
 * is a local tile that carries its own capture; routing it through
 * MediaStreamContext would render your primary camera in both tiles.
 *
 * `videoOn`, not `cameraOn`: a camera this viewer has paused ("pause incoming
 * videos", or hidden on that tile) draws the avatar just as a camera that is
 * off does.
 */
export function tileHasVideo(participant: Pick<Participant, "isLocal" | "stream" | "videoOn">): boolean {
  return participant.isLocal && !participant.stream
    ? participant.videoOn
    : participant.videoOn && !!participant.stream
}

/**
 * Whether a tile offers its full-screen button.
 *
 * Only with a picture to show: full screen on a camera-off tile filled the
 * screen with the avatar, and the button implied there was video to see.
 * A tile that is already full screen keeps the button whatever its camera
 * does, because the same button is the way out, and a phone has no Esc.
 */
export function tileOffersFullscreen(hasVideo: boolean, isFullscreen: boolean): boolean {
  return hasVideo || isFullscreen
}
